import Breadcrumbs from 'components/Breadcrumbs'
import LoadingIcon from 'components/LoadingIcon'
import SessionForm from 'components/SessionForm'
import { createSession, fetchSession, updateSession } from 'config/api'
import routes from 'config/routes'
import { useBankrolls } from 'hooks'
import PropTypes from 'prop-types'
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useToasts } from 'react-toast-notifications'
import { parseError, sleep } from 'utils'
import BankrollFilter from 'components/BankrollFilter'

function Session({
  location: { pathname, state: routerState },
  history,
  match,
}) {
  const { addToast } = useToasts()
  const { currentBankroll, bankrolls, selectBankroll, createBankroll } =
    useBankrolls()
  const [editing, setEditing] = React.useState(false)
  const [session, setSession] = React.useState()
  const qClient = useQueryClient()

  const qSession = useQuery(
    ['sessions', match.params.id],
    async () => {
      await sleep(500)
      return fetchSession(currentBankroll.id, match.params.id)
    },
    {
      onSuccess: data => {
        setSession(data)
      },
      onError: err => {
        console.error('Error retrieving session')
        console.error(err)
        history.push(routes.sessions)
      },
      enabled: pathname !== routes.sessionNew,
    },
  )

  const mSession = useMutation(
    ({ action, id, ...session }) => {
      switch (action) {
        case 'create':
          return createSession(currentBankroll.id, session)
        case 'update':
          return updateSession(currentBankroll.id, id, session)
        default:
          throw new Error('unknown action')
      }
    },
    {
      onSuccess: () => {
        qClient.invalidateQueries('bankrolls')
        qClient.invalidateQueries('sessions')
      },
    },
  )

  React.useEffect(() => {
    setEditing(pathname !== routes.sessionNew)
    if (routerState && routerState.session) {
      setSession(routerState.session)
    }
  }, [history, match, pathname, routerState])

  const submit = async session => {
    try {
      await mSession.mutateAsync({
        ...session,
        action: editing ? 'update' : 'create',
      })
      addToast(`${editing ? 'Updated' : 'Created'} session`, {
        appearance: 'success',
        autoDismiss: true,
      })
      history.push(routes.sessions)
    } catch (err) {
      const { non_field_errors } = parseError(err)
      addToast(
        non_field_errors || `Error ${editing ? 'editing' : 'adding'} session`,
        {
          appearance: 'error',
          autoDismiss: true,
        },
      )
      throw err
    }
  }

  const initial = session
    ? {
        ...session,
        hours:
          session.duration === undefined
            ? session.hours
            : parseInt(session.duration.split(':')[0]),
        minutes:
          session.duration === undefined
            ? session.minutes
            : parseInt(session.duration.split(':')[1]),
      }
    : null

  return (
    <div id="session">
      <Breadcrumbs
        crumbs={[
          ['DASHBOARD', routes.dashboard],
          ['SESSIONS', routes.sessions],
          [editing ? 'EDIT' : 'ADD', ''],
        ]}
      />
      <Row>
        <Col className="d-flex flex-wrap justify-content-start justify-content-md-between">
          <BankrollFilter
            current={currentBankroll}
            choices={bankrolls}
            onChange={selectBankroll}
            onCreate={createBankroll}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mt-3">
          {qSession.isLoading ? (
            <LoadingIcon />
          ) : (
            <SessionForm
              onSubmit={submit}
              editing={editing}
              initial={initial}
              customFields={currentBankroll.custom_session_fields}
              showEvField={!currentBankroll.ev_disabled}
              showPlayerField={currentBankroll.shared_users.length > 0}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

Session.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
}

export default Session
